/**
 * @description Shared date-preset math for Reporting and invoice-list filters.
 * @dependencies date-fns format
 * @notes Presets match InvoiceList (today / week / month / quarter / year).
 * Period drill matches Postgres date_trunc week/month and stays on the
 * Reporting role prefix (/merchant vs /so).
 */

import { format } from 'date-fns';

export const REPORTING_DATE_PRESETS = Object.freeze(['today', 'week', 'month', 'quarter', 'year']);

/**
 * @param {string} preset
 * @param {Date} [today]
 * @returns {{ dateFrom: string, dateTo: string }}
 */
export function resolveDatePreset(preset, today = new Date()) {
  const formatDate = (date) => format(date, 'yyyy-MM-dd');
  const dateTo = formatDate(today);

  switch (preset) {
    case 'today':
      return { dateFrom: dateTo, dateTo };
    case 'week': {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      return { dateFrom: formatDate(weekStart), dateTo };
    }
    case 'month':
      return {
        dateFrom: formatDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        dateTo
      };
    case 'quarter': {
      const quarter = Math.floor(today.getMonth() / 3);
      return {
        dateFrom: formatDate(new Date(today.getFullYear(), quarter * 3, 1)),
        dateTo
      };
    }
    case 'year':
      return {
        dateFrom: formatDate(new Date(today.getFullYear(), 0, 1)),
        dateTo
      };
    default:
      return { dateFrom: dateTo, dateTo };
  }
}

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function toUtcDate(ymd) {
  const [year, month, day] = ymd.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
}

function formatYmdUtc(date) {
  return date.toISOString().slice(0, 10);
}

function clampYmd(value, min, max) {
  let next = value;
  if (min && next < min) next = min;
  if (max && next > max) next = max;
  return next;
}

/**
 * Inclusive paid-invoice window for one Sales by Period bar.
 * Matches Postgres date_trunc: week = ISO Monday–Sunday, month = 1st–last.
 * Clamped to the report filter so the invoice list matches the bar totals.
 *
 * @param {string} periodStart
 * @param {'day'|'week'|'month'|string} period
 * @param {{ dateFrom?: string, dateTo?: string }} [bounds]
 * @returns {{ dateFrom: string, dateTo: string }}
 */
export function periodDrilldownRange(periodStart, period, bounds = {}) {
  const start = String(periodStart || '').slice(0, 10);
  if (!YMD_RE.test(start)) return { dateFrom: '', dateTo: '' };

  let dateFrom = start;
  let dateTo = start;
  if (period === 'week') {
    const end = toUtcDate(start);
    end.setUTCDate(end.getUTCDate() + 6);
    dateTo = formatYmdUtc(end);
  } else if (period === 'month') {
    const [year, month] = start.split('-').map(Number);
    dateTo = formatYmdUtc(new Date(Date.UTC(year, month, 0)));
  }

  dateFrom = clampYmd(dateFrom, bounds.dateFrom || null, bounds.dateTo || null);
  dateTo = clampYmd(dateTo, bounds.dateFrom || null, bounds.dateTo || null);
  if (dateFrom && dateTo && dateFrom > dateTo) {
    return { dateFrom: bounds.dateFrom || dateFrom, dateTo: bounds.dateTo || dateTo };
  }
  return { dateFrom, dateTo };
}

/**
 * Keep drill-down on the same role prefix as Reporting.
 * /so/reporting → /so/transactions; otherwise /merchant/transactions.
 *
 * @param {string} [pathname]
 * @returns {string}
 */
export function transactionsPathForReporting(pathname = '') {
  return String(pathname).startsWith('/so/') ? '/so/transactions' : '/merchant/transactions';
}

/**
 * Build invoice-list drill-down query for a paid period.
 * @param {{ dateFrom: string, dateTo: string, customerId?: string|number|null }} filters
 * @param {string} [pathname] Reporting location (`/merchant/reporting` or `/so/reporting`)
 */
export function buildPaidInvoiceDrilldownPath(filters, pathname) {
  const params = new URLSearchParams();
  params.set('tab', 'invoices');
  params.set('paymentStatus', 'paid');
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.customerId) params.set('customerId', String(filters.customerId));
  return `${transactionsPathForReporting(pathname)}?${params.toString()}`;
}
